import React from 'react';

import i18n from '@client/utils/i18n';
import { LinkTypeChip } from '@ledium/ui-common';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import type { ZoneChannel } from './ZoneWizardStep4';

type Scenario = 'copy' | 'link-sync' | 'link-independent';

interface ZoneWizardStep5Props {
    channelMap: Record<string, number | undefined>;
    newName: string;
    scenario: Scenario;
    channels: ZoneChannel[];
}

function SummarySection({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                {label}
            </Typography>
            {children}
        </Box>
    );
}

function ZoneWizardStep5({ channelMap, newName, scenario, channels }: ZoneWizardStep5Props) {
    let description = i18n('copy_zone_step5_description');
    if (scenario === 'link-sync') {
        description = i18n('copy_zone_link_sync_step5_description');
    } else if (scenario === 'link-independent') {
        description = i18n('copy_zone_link_independent_step5_description');
    }
    const nameLabel = scenario === 'copy' ? i18n('copy_zone_step5_new_zone') : i18n('copy_zone_link_name');

    return (
        <>
            <Typography paragraph>{description as string}</Typography>

            <SummarySection label={nameLabel as string}>
                <Typography>{newName}</Typography>
            </SummarySection>

            {scenario !== 'copy' && (
                <SummarySection label={i18n('linked_zone_type_label') as string}>
                    <LinkTypeChip type={scenario === 'link-sync' ? 'mirror' : 'independent'} />
                </SummarySection>
            )}

            {channels.length > 0 && (
                <SummarySection label={i18n('copy_zone_link_channel_mapping') as string}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{i18n('copy_zone_channel_name') as string}</TableCell>
                                <TableCell>{i18n('copy_zone_original_dmx') as string}</TableCell>
                                <TableCell>{i18n('copy_zone_new_dmx') as string}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {channels.map((ch) => (
                                <TableRow key={ch.id}>
                                    <TableCell>{ch.name}</TableCell>
                                    <TableCell>{ch.dmxAddress}</TableCell>
                                    <TableCell>{channelMap[ch.id] ?? '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SummarySection>
            )}
        </>
    );
}

export default ZoneWizardStep5;
